import { Lock } from 'app/assets/icons'
import { media, ResponsiveContainer } from 'app/assets/styles'
import { ErrorFocus } from 'app/common/error-focus'
import { Loader } from 'app/common/loader'
import { Recaptcha } from 'app/common/recaptcha'
import { Text } from 'app/common/typography'
import { Layout, SigninSignup } from 'app/ecommerce/common'
import {
    ButtonFooter,
    CartUserPersonalInfos,
    PaymentChoice
} from 'app/ecommerce/common/components'
import { Form, Formik } from 'formik'
import _ from 'lodash'
import type { Dispatch } from 'react'
import React, { Component, Fragment } from 'react'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import { colors } from 'shared/assets/styles'
import {
    type OrderInfos,
    type PaymentMethodData,
    type PaymentMethodsResponseInterface,
    type PaymentResponse,
    type PurchasableItem
} from 'shared/store/ecommerce/order'
import type { ECommerceMetadata, EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata'
import type {
    CreditCardInfo,
    LoginForm,
    MetaShortDTO,
    ProfileUserForm,
    Restaurant
} from 'shared/types'
import { formatPrice, neptingStatus, orderPickUpType } from 'shared/utils'
import { featureCodes, PaymentProvider, regex } from 'shared/utils/const'
import styled from 'styled-components'
import { boolean, object, string } from 'yup'
import { AdyenCheckout } from '../../components/cart-user-infos/AdyenCheckout'
import { AdyenCheckout3DS } from '../../components/cart-user-infos/AdyenCheckout3DS'
import { creditCardType } from 'shared/utils/ecommerce'
import {
    formatPhoneNumberToE164,
    validatePhoneNumber
} from '../../../../common/utils'

const { lightMayo, text } = colors
const { PAYMENT_SUCCESS } = neptingStatus
const { TABLE_ORDER } = orderPickUpType

type Props = {
    authenticated: boolean,
    userData: LoginForm,
    email: string,
    firstname: string,
    phone: string,
    carColor: string,
    licensePlateNumber: string,
    navigate: (to: string, state?: any) => void,
    intl: IntlShape,
    finalPrice: number,
    pickUpParking: boolean,
    products: Array<PurchasableItem>,
    isPaymentWithoutLoginEnabled: boolean,
    mealVoucherService: boolean,
    neptingStatus?: string,
    fetchProfileData: () => void,
    updateProfile: (form: ProfileUserForm) => void,
    sendCurrentOrder: (
        data: OrderInfos
    ) => Promise<?{ token?: string, orderId?: string }>,
    oneClickPayment: (cbid: string) => void,
    getPaymentUrl: (selectedCardId?: string, cardEnrolment: boolean) => void,
    waitForOrderStatusChange: () => void,
    sendECommerceMeta: ECommerceMetadata => void,
    pageMetadata: (payload: MetaShortDTO) => void,
    sendMeta: EventMetadata => void,
    error?: {
        message: string,
        reason?: string
    },
    handleAdyenPayment: (data: PaymentMethodData) => void,
    manageError: (error: string) => void,
    setError: Dispatch<{ message?: string, reason?: string }>,
    isLoading: Boolean,
    setIsLoading: Dispatch<Boolean>,
    adyen: {
        paymentMethods: PaymentMethodsResponseInterface,
        paymentResponse: PaymentResponse
    },
    restaurant: Restaurant,
    clearError: () => void,
    currentOrder: any,
    storedCards: Array<CreditCardInfo>,
    cardIdSelected: string
}

type State = {
    showCaptcha: boolean
}

class CartUserInfos extends Component<Props, State> {
    recaptchaPromise: Promise<string>
    resolve: any
    reject: any

    constructor(props: Props) {
        super(props)
        this.state = {
            showCaptcha: false
        }
    }

    async componentDidMount() {
        const {
            authenticated,
            fetchProfileData,
            products,
            sendECommerceMeta,
            pageMetadata,
            neptingStatus,
            manageError,
            setIsLoading,
            currentOrder
        } = this.props
        sendECommerceMeta({
            type: 'checkout',
            data: {
                step: 2,
                products
            }
        })
        pageMetadata({})
        this.initRecaptchaPromise()
        if (authenticated) {
            setIsLoading(true)
            await fetchProfileData()
            setIsLoading(false)
        }
        if (neptingStatus) {
            if (neptingStatus === PAYMENT_SUCCESS) {
                if (currentOrder && currentOrder.pickUpType === TABLE_ORDER) {
                    return
                }
                this.waitForOrderStatusChange()
            } else {
                manageError(neptingStatus)
            }
        }
    }

    async componentDidUpdate(prevProps: Props, prevState: State) {
        const { authenticated, fetchProfileData, setIsLoading } = this.props
        if (prevProps.authenticated !== authenticated && authenticated) {
            setIsLoading(true)
            await fetchProfileData()
            setIsLoading(false)
        }
    }

    initRecaptchaPromise = () => {
        this.recaptchaPromise = new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }

    verifyCallback = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            this.resolve(recaptchaToken)
        } else {
            this.reject('Error getting recaptcha token')
        }
    }

    sendMetaData = (cardIdSelected: string, saveCard: boolean) => {
        const { sendMeta } = this.props
        if (saveCard) {
            sendMeta({
                ...eventsDefinitions.ECOMM_CARD_REGISTRATION,
                event_label: cardIdSelected || ''
            })
            sendMeta({
                ...eventsDefinitions.GA4_ECOMM_CARD_REGISTRATION,
                creditcard_type: cardIdSelected || ''
            })
        }
    }

    formSubscription = () => {
        const { pickUpParking } = this.props
        const mandatory = 'form.error.field.mandatory'
        let validationShape = {
            email: string()
                .required(mandatory)
                .matches(regex.EMAIL, {
                    message: 'form.error.regex.email',
                    excludeEmptyString: true
                }),
            phone: string()
                .required(mandatory)
                .test('phone-number', 'form.error.field.invalid', value =>
                    validatePhoneNumber(value)
                )
                .transform(value =>
                    value ? formatPhoneNumberToE164(value) : value
                ),

            // For now, only CaC is available in the web, so the firstname is always optional
            firstname: string()
                .matches(
                    regex.EXCLUDE_SPECIAL_CHARACTERS,
                    'form.error.field.special.characters.forbidden'
                )
                .nullable()
        }

        if (pickUpParking) {
            validationShape = {
                ...validationShape,
                licensePlateNumber: string()
                    .required(mandatory)
                    .matches(
                        regex.LICENCE_PLATE,
                        'form.error.regex.licenceplate'
                    ),
                carColor: string()
                    .max(25, 'form.error.regex.carcolor')
                    .required(mandatory),
                licensePlateEnrolment: boolean().required(mandatory)
            }
        }
        validationShape = {
            ...validationShape,
            cgv: boolean()
                .required(mandatory)
                .oneOf([true], 'clickandcollect.userinfos.cgv.error')
        }
        return object().shape(validationShape)
    }

    handleSubmit = async (values: any, actions: any, initialValues: any) => {
        const {
            finalPrice,
            updateProfile,
            sendCurrentOrder,
            pickUpParking,
            storedCards,
            intl,
            manageError,
            clearError,
            restaurant,
            authenticated,
            history,
            availableFeatures
        } = this.props
        const { cgv, ...initData } = initialValues
        const {
            carColor,
            email,
            firstname,
            licensePlateEnrolment,
            licensePlateNumber,
            phone,
            saveCard,
            cardIdSelected
        } = values
        let userInfos = {
            email: email.trim(),
            firstname: firstname.trim(),
            phone: formatPhoneNumberToE164(phone)
        }

        clearError()

        let enableOneClickPaymentV1 =
            restaurant.paymentServiceProvider !== PaymentProvider.ADYEN &&
            !!storedCards.find(
                creditCard => creditCard.id === cardIdSelected
            ) &&
            availableFeatures.includes(featureCodes.NEPTING_ONE_CLICK_V1)

        try {
            this.setState({ showCaptcha: true })
            if (pickUpParking) {
                const licencePlateQuery = `licensePlateNumber=${licensePlateNumber
                    .trim()
                    .toString()
                    .toUpperCase()
                    .substr(0, 7)}`
                userInfos = {
                    ...userInfos,
                    pickUpInfo: carColor
                        ? licencePlateQuery + `&carColor=${carColor.trim()}`
                        : licencePlateQuery,
                    licensePlateEnrolment
                }
            }
            const recaptchaToken = await this.recaptchaPromise
            const deviceCheckPayload = {
                queen: recaptchaToken
            }

            const { orderId, token } = await sendCurrentOrder({
                userInfos,
                deviceCheckPayload
            })

            if (finalPrice === 0) {
                const path = intl.formatMessage({
                    id: 'routes.clickandcollect.cart.confirmation'
                })
                history.replace(`${path}?id=${orderId}&token=${token}`)
            } else if (enableOneClickPaymentV1) {
                await this.handleOneClickPayment(cardIdSelected)
            } else {
                await this.handlePayment(cardIdSelected, saveCard, values)
            }
        } catch (error) {
            manageError(error)
        } finally {
            this.setState({ showCaptcha: false })
            this.initRecaptchaPromise()
            if (authenticated && !_.isEqual(userInfos, initData)) {
                updateProfile(userInfos)
            }
            actions.setSubmitting(false)
        }
    }

    handlePayment = async (
        cardIdSelected: string,
        saveCard: boolean,
        values: any
    ) => {
        const {
            getPaymentUrl,
            manageError,
            handleAdyenPayment,
            restaurant,
            setIsLoading
        } = this.props
        setIsLoading(true)
        // Adyen
        if (restaurant.paymentServiceProvider === PaymentProvider.ADYEN) {
            handleAdyenPayment(values.adyenData)
        } else {
            // Nepting
            try {
                const selectedCardId = Object.values(creditCardType).includes(
                    cardIdSelected
                )
                    ? undefined
                    : cardIdSelected

                //If the user doesn't have any saved card, the cardIdSelected will have a creditCardType value instead of a real cardId,
                //It means they need to be redirected to the payment page to add a new card instead of using oneclick payment
                //This creditCardType value must not be sent to the backend, instead we send undefined

                const paymentUrl = await getPaymentUrl(selectedCardId, saveCard)
                this.sendMetaData(cardIdSelected, saveCard)
                window.location = paymentUrl
            } catch (error) {
                setIsLoading(false)
                manageError(error)
            }
        }
    }

    handleOneClickPayment = async (cardIdSelected: string) => {
        const {
            oneClickPayment,
            intl,
            manageError,
            currentOrder,
            history
        } = this.props
        const { token, id } = currentOrder || {}
        try {
            cardIdSelected && (await oneClickPayment(cardIdSelected))
            const path = intl.formatMessage({
                id: 'routes.clickandcollect.cart.confirmation'
            })
            history.replace(`${path}?id=${id}&token=${token}`)
        } catch (error) {
            manageError(error)
        }
    }

    waitForOrderStatusChange = async () => {
        const {
            waitForOrderStatusChange,
            intl,
            manageError,
            setIsLoading,
            currentOrder,
            history
        } = this.props
        const { token, id } = currentOrder || {}
        try {
            setIsLoading(true)
            const path = intl.formatMessage({
                id: 'routes.clickandcollect.cart.confirmation'
            })
            history.replace(`${path}?id=${id}&token=${token}`)
            await waitForOrderStatusChange()
        } catch (error) {
            setIsLoading(false)
            manageError(error)
        }
    }

    renderPayment = () => {
        return (
            <Payment>
                <Lock />
                <FormattedMessage id="clickandcollect.payment.secured">
                    {(text: string) => <Text>{text}</Text>}
                </FormattedMessage>
            </Payment>
        )
    }

    // Redirection is handled in PaymentWrapper
    handleAdyen3DSSuccess = () => {
        this.props.setIsLoading(true)
    }

    render() {
        const {
            authenticated,
            email,
            firstname,
            phone,
            carColor,
            licensePlateNumber,
            pickUpParking,
            finalPrice,

            isPaymentWithoutLoginEnabled,
            mealVoucherService,
            intl,
            restaurant,
            adyen,
            isLoading,
            setError,
            cardIdSelected,
            storedCards
        } = this.props

        const isAdyenRestaurant =
            restaurant?.paymentServiceProvider === PaymentProvider.ADYEN

        const { showCaptcha } = this.state

        const initialValues = {
            email,
            firstname,
            phone,
            cgv: true,
            carColor,
            licensePlateNumber,
            licensePlateEnrolment: false,
            saveCard: false,
            cardIdSelected
        }

        const adyenAction = adyen?.paymentResponse?.action

        if (!isPaymentWithoutLoginEnabled && !!authenticated) {
            return (
                <Layout background="plain" border cart currentStep={2}>
                    <Main>
                        <SigninSignup
                            fromUrl={intl.formatMessage({
                                id: 'routes.clickandcollect.cart.user.infos'
                            })}
                            fromEcommerce
                        />
                    </Main>
                </Layout>
            )
        }

        return (
            <Layout
                background="plain"
                backgroundColor={lightMayo}
                border
                cart
                currentStep={3}>
                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    onSubmit={(values, actions) =>
                        this.handleSubmit(values, actions, initialValues)
                    }
                    validationSchema={this.formSubscription}>
                    {({
                        handleChange,
                        handleSubmit,
                        handleBlur,
                        values,
                        errors,
                        touched,
                        setFieldValue,
                        isSubmitting,
                        isValidating
                    }) => (
                        <Fragment>
                            <Main>
                                <Form>
                                    <StyledResponsiveContainer>
                                        {adyenAction ? (
                                            <CenterContainer>
                                                <AdyenCheckout3DS
                                                    action={adyenAction}
                                                    setError={setError}
                                                    storedCards={storedCards}
                                                    onSuccess={
                                                        this
                                                            .handleAdyen3DSSuccess
                                                    }
                                                />
                                            </CenterContainer>
                                        ) : (
                                            <Loader
                                                loading={
                                                    isLoading || isSubmitting
                                                }
                                                color={text}
                                                size="large"
                                                loaderMessage={
                                                    isSubmitting
                                                        ? 'clickandcollect.waiting.order.validation'
                                                        : 'clickandcollect.waiting.order.wait'
                                                }
                                                children={
                                                    <Fragment>
                                                        <Section>
                                                            <CartUserPersonalInfos
                                                                formik={{
                                                                    values,
                                                                    errors,
                                                                    touched,
                                                                    setFieldValue,
                                                                    handleChange,
                                                                    handleBlur
                                                                }}
                                                                authenticated={
                                                                    authenticated
                                                                }
                                                                pickUpParking={
                                                                    pickUpParking
                                                                }
                                                            />
                                                        </Section>
                                                        {finalPrice !== 0 && (
                                                            <Section>
                                                                {isAdyenRestaurant ? (
                                                                    <AdyenCheckout
                                                                        setError={
                                                                            this
                                                                                .props
                                                                                .setError
                                                                        }
                                                                        formik={{
                                                                            values,
                                                                            setFieldValue
                                                                        }}
                                                                        intl={
                                                                            intl
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <PaymentChoice
                                                                        cards={
                                                                            storedCards
                                                                        }
                                                                        mealVoucherService={
                                                                            mealVoucherService
                                                                        }
                                                                        restaurant={
                                                                            restaurant
                                                                        }
                                                                        formik={{
                                                                            values,
                                                                            setFieldValue
                                                                        }}
                                                                        setError
                                                                        authenticated={
                                                                            authenticated
                                                                        }
                                                                    />
                                                                )}
                                                            </Section>
                                                        )}
                                                        {this.renderPayment()}
                                                    </Fragment>
                                                }
                                            />
                                        )}
                                    </StyledResponsiveContainer>
                                    {showCaptcha && (
                                        <Recaptcha
                                            action="WebCCCartUserInfo"
                                            verifyCallback={this.verifyCallback}
                                        />
                                    )}
                                    <ButtonFooter
                                        labelKey="clickandcollect.validate.cart.footer"
                                        labelParams={{
                                            finalPrice: formatPrice(finalPrice)
                                        }}
                                        disabled={
                                            isSubmitting ||
                                            isLoading ||
                                            (isAdyenRestaurant &&
                                                finalPrice !== 0 &&
                                                (adyenAction ||
                                                    !values.adyenData?.isValid))
                                        }
                                        onClick={handleSubmit}
                                        showTermsOfSales
                                        shouldStick
                                    />
                                    <ErrorFocus
                                        isSubmitting={isSubmitting}
                                        isValidating={isValidating}
                                        errors={errors}
                                    />
                                </Form>
                            </Main>
                        </Fragment>
                    )}
                </Formik>
            </Layout>
        )
    }
}

const CenterContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
`

const Main = styled.section`
    margin-bottom: 175px;
    padding: 15px 0;
    margin-top: 80px;
    ${media.medium`
        padding: 50px 0;
    `}
`

const Section = styled.div`
    border-radius: 12px;
    padding: 20px 30px;
    margin-bottom: 20px;
    background-color: white;
    ${media.medium`
            padding: 30px 30px 35px;
    `}
`

const StyledResponsiveContainer = styled(ResponsiveContainer)`
    margin-left: auto;
    margin-right: auto;
    max-width: 710px;
`

const Payment = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
`

export const CartUserInfosComponent = injectIntl(CartUserInfos)
